import { useEffect, useState, type FormEvent } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { navLinks, marqueeTicker } from "@/mocks/content";

const linkBase =
  "px-1 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/news?q=${encodeURIComponent(value)}` : "/news");
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="w-full overflow-hidden bg-primary-500">
        <div className="mx-auto flex w-full items-center gap-4 px-4 py-1.5 md:px-6">
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-primary-950/25 px-2.5 py-0.5 text-xs font-medium text-background-50">
            <i className="ri-fire-fill text-[13px]" aria-hidden="true" />
            实时速递
          </span>
          <div className="relative w-full overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-8">
              {[...marqueeTicker, ...marqueeTicker].map((item, index) => (
                <span
                  key={`${item}-${index}`}
                  className="whitespace-nowrap text-xs text-background-50/90"
                >
                  · {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur-md">
        <div className="mx-auto flex w-full items-center gap-4 px-4 py-3 md:px-6">
          <NavLink to="/" className="flex shrink-0 items-center gap-2.5" onClick={() => setMenuOpen(false)}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-fire-line text-lg" aria-hidden="true" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
                吃瓜日报
              </span>
              <span className="mt-0.5 text-[10px] font-medium tracking-[0.2em] text-foreground-500">
                CHIGUA DAILY
              </span>
            </span>
          </NavLink>

          <nav aria-label="主导航" className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `${linkBase} rounded-md ${
                    isActive
                      ? "text-primary-600"
                      : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <form
            onSubmit={handleSearch}
            role="search"
            className="ml-auto hidden items-center gap-2 rounded-full border border-background-300/70 bg-background-100/70 px-3 py-1.5 md:flex"
          >
            <label htmlFor="header-search" className="sr-only">
              搜索瓜料
            </label>
            <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm" aria-hidden="true" />
            </span>
            <input
              id="header-search"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索网红、事件、关键词"
              className="w-40 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-52"
            />
            <button
              type="submit"
              className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors duration-200 hover:bg-secondary-200"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-expanded={menuOpen}
            aria-label="打开菜单"
            className="ml-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-background-300/70 text-foreground-800 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200/80 bg-background-50 px-4 pb-5 pt-4 md:hidden">
            <form onSubmit={handleSearch} role="search" className="mb-4 flex items-center gap-2 rounded-lg border border-background-300/70 bg-background-100 px-3 py-2">
              <label htmlFor="mobile-search" className="sr-only">
                搜索瓜料
              </label>
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" aria-hidden="true" />
              </span>
              <input
                id="mobile-search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索网红、事件、关键词"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </form>
            <nav aria-label="移动端导航" className="flex flex-col">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `border-b border-background-200/70 py-3 text-sm font-medium ${
                      isActive ? "text-primary-600" : "text-foreground-700"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}